import { useCheckResponse } from '@src/hooks/useCheckResponse';
import { useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { IDynamicFormProps } from '../DynamicForm';
import { IDynamicFormGroup, IDynamicFormRow } from '../DynamicForm.types';
import FormRow, { REDUNDUNT_FIELD } from '../FormRow';
import { generatePermission } from '../utils/generatePermission';

export function useDynamicForm(props: IDynamicFormProps) {
    const { checkResponse } = useCheckResponse();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        clearErrors,
        setError,
        trigger,
        formState,
        reset,
    } = useForm({
        shouldFocusError: true,
        ...props.useFormOptions,
        defaultValues:
            props?.itemId && !props.formData?.['_id']
                ? async () => {
                      try {
                          const data = await props.callApi({
                              id: props?.itemId,
                          });
                          const { success } = checkResponse(data);
                          if (success) {
                              return data.data;
                          }
                      } catch (error) {
                          checkResponse({ error });
                      }
                  }
                : props.formData || {},
    });

    const { errors } = formState;

    useEffect(() => {
        if (props.resetFormData && props.formData) {
            reset(props.formData);
        }
    }, [props.resetFormData, props.formData]);

    const renderFormGroups = (groups?: IDynamicFormGroup[]) => {
        if (!groups) return null;
        return groups.map((formGroup: IDynamicFormGroup, index: number) => (
            <div key={props.formKeyName + 'form_group' + index}>
                {formGroup.title && (
                    <div className="mb-6 sm:mb-8">
                        <p className="text-xl text-secondary font-bold">
                            {formGroup.title}
                        </p>
                        {formGroup.description && (
                            <p className="text-lg text-primary mt-3 font-normal">
                                {formGroup.description}
                            </p>
                        )}
                    </div>
                )}
                <div className={formGroup.className}>
                    {renderFormRows(formGroup.formRows)}
                </div>
            </div>
        ));
    };

    const renderFormRows = (rows?: IDynamicFormRow[]) => {
        if (!rows) return null;
        return rows.map((formRow, index) => {
            const { isDisabled, isHidden } = generatePermission(
                props.disabledFields,
                props.subscriptionKey,
                formRow.keyName,
                formRow.disabled
            );
            if (isHidden) {
                return null;
            }

            if (
                formState.defaultValues?.[formRow.keyName] === REDUNDUNT_FIELD
            ) {
                return null;
            }

            if (formRow.nestedInputs) {
                const { Component } = formRow;

                return (
                    <Component
                        key={props.formKeyName + '-' + formRow.keyName + index}
                        register={register}
                        watch={watch}
                        errors={
                            !formRow.keyName
                                ? errors
                                : (errors[formRow.keyName] ??
                                  errors[formRow.keyName.split('.')?.[0]])
                        }
                        control={control}
                        setValue={setValue}
                        value={
                            formRow.keyName
                                ? props.formData?.[formRow.keyName]
                                : formState.defaultValues
                        }
                        disabled={formRow.disabled}
                        keyName={formRow.keyName}
                        label={formRow.label}
                        formProps={{ ...formRow?.formProps }}
                        reset={reset}
                    />
                );
            }

            return (
                <FormRow
                    {...formRow}
                    key={props.formKeyName + '-' + formRow.keyName + index}
                    register={register}
                    registerOptions={formRow.registerOptions}
                    label={formRow.label}
                    control={control}
                    value={props.formData?.[formRow.keyName]}
                    errors={errors[formRow.keyName] as FieldError}
                    setValue={setValue}
                    getValues={getValues}
                    watch={watch}
                    disabled={props.disableAll || isDisabled}
                    setError={setError}
                    triggerValidation={trigger}
                    clearError={clearErrors}
                    reset={reset}
                />
            );
        });
    };

    const onSubmit = (data: Record<string, any>) => {
        Object.keys(data).forEach((key) => {
            if (data[key] === REDUNDUNT_FIELD) {
                delete data[key];
            }
        });
        props.onHandleSubmit(data);
    };

    /**Renders the form rows */
    const createForm = () => {
        return [
            renderFormGroups(props.formGroups),
            renderFormRows(props.formRows),
        ];
    };

    return {
        getValues,
        onSubmit,
        handleSubmit,
        control,
        createForm,
        reset,
        trigger,
        setError,
        watch,
        register,
        setValue,
        clearErrors,
        formState,
        errors,
    };
}
